import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Stack,
  Typography,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { CreateProductoInput, UpdateProductoInput } from '../../types';
import { ImagenProducto } from '../../types/producto.types';
import { useCategorias } from '../../hooks/useCategorias';
import { ProductoCategorySelector } from '../ProductoCategorySelector';
import { ImagenesProducto } from '../../form/components/imagenes/ImagenesProducto';

interface ProductFormProps {
  formData: CreateProductoInput | UpdateProductoInput;
  loading: boolean;
  error: string | null;
  isEditing: boolean;
  onChange: (field: string, value: any) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel?: () => void;
  imagenes?: ImagenProducto[];
  onImagenesChange?: (imagenes: ImagenProducto[]) => void;
  productoId?: number;
}

export function ProductForm({
  formData,
  loading,
  error,
  isEditing,
  onChange,
  onSubmit,
  onCancel,
  imagenes = [],
  onImagenesChange,
  productoId,
}: ProductFormProps) {
  const data = formData as any;
  const { categorias, loading: loadingCategorias } = useCategorias();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleCategoryChange = (categoriaId: number | null, subcategoriaId: number | null) => {
    onChange('categoriaId', categoriaId);
    onChange('subcategoriaId', subcategoriaId);
  };

  return (
    <Box component="form" onSubmit={onSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {error && <Alert severity="error">{error}</Alert>}

      {/* Código - Solo lectura en edición */}
      {isEditing ? (
        <Box>
          <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.5 }}>
            Código
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {data.codigo}
          </Typography>
        </Box>
      ) : (
        <TextField
          fullWidth
          label="Código"
          value={data.codigo || ''}
          onChange={(e) => onChange('codigo', e.target.value)}
          required
          size="small"
        />
      )}

      {/* Nombre */}
      <TextField
        fullWidth
        label="Nombre"
        value={data.nombre || ''}
        onChange={(e) => onChange('nombre', e.target.value)}
        required
        size="small"
      />

      {/* Descripción */}
      <TextField
        fullWidth
        label="Descripción"
        value={data.descripcion || ''}
        onChange={(e) => onChange('descripcion', e.target.value)}
        multiline
        rows={2}
        size="small"
      />

      {/* Precios en 2 columnas */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <TextField
          fullWidth
          label="Precio Base"
          type="number"
          value={data.precioBase || 0}
          onChange={(e) => onChange('precioBase', parseInt(e.target.value) || 0)}
          required
          inputProps={{ step: 1, min: 0 }}
          size="small"
        />
        <TextField
          fullWidth
          label="Precio Venta"
          type="number"
          value={data.precioVenta || 0}
          onChange={(e) => onChange('precioVenta', parseInt(e.target.value) || 0)}
          required
          inputProps={{ step: 1, min: 0 }}
          size="small"
        />
      </Box>

      {/* Cantidad Stock */}
      <TextField
        fullWidth
        label="Cantidad en Stock"
        type="number"
        value={data.cantidadStock || 0}
        onChange={(e) => onChange('cantidadStock', parseInt(e.target.value) || 0)}
        required
        inputProps={{ step: 1, min: 0 }}
        size="small"
      />

      {/* Categoría y Subcategoría */}
      {isMounted && (
        <Box sx={{ my: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Categoría y Subcategoría
          </Typography>
          <ProductoCategorySelector
            categoriaId={data.categoriaId || null}
            subcategoriaId={data.subcategoriaId || null}
            onChange={handleCategoryChange}
            categorias={categorias}
            loading={loadingCategorias}
          />
        </Box>
      )}

      {/* Activo - Solo en edición */}
      {isEditing && (
        <FormControlLabel
          control={
            <Switch
              checked={data.activo || false}
              onChange={(e) => onChange('activo', e.target.checked)}
            />
          }
          label="Producto Activo"
        />
      )}

      {/* Imágenes - Solo en edición */}
      {isEditing && productoId && onImagenesChange && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Imágenes del Producto
          </Typography>
          <ImagenesProducto
            imagenes={imagenes}
            onImagenesChange={onImagenesChange}
            maxImagenes={5}
            maxTamañoMB={10}
            transitionEffect="cube"
            transitionDuration={400}
          />
        </Box>
      )}

      {/* Botones de acción */}
      <Stack direction="row" spacing={2} sx={{ mt: 3, pt: 2, borderTop: '1px solid #eee' }}>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {isEditing ? 'Actualizar' : 'Crear'}
        </Button>
        {onCancel && (
          <Button variant="outlined" onClick={onCancel} disabled={loading}>
            Cancelar
          </Button>
        )}
      </Stack>
    </Box>
  );
}
